import { useState } from 'react';

const STATUSES = ['Not Started', 'Preparing', 'In Progress', 'On Hold', 'Completed'];
const PRIORITIES = ['Low', 'Medium', 'High'];

export default function ViewModal({ view, onSave, onClose, folders, tags }) {
  const criteria = view?.criteria || {};

  const [name, setName] = useState(view?.name || '');
  const [selectedStatuses, setSelectedStatuses] = useState(criteria.status || []);
  const [selectedPriorities, setSelectedPriorities] = useState(criteria.priority || []);
  const [selectedFolderIds, setSelectedFolderIds] = useState(criteria.folder_ids || []);
  const [selectedTagIds, setSelectedTagIds] = useState(criteria.tag_ids || []);
  const [selectedItemTypes, setSelectedItemTypes] = useState(criteria.item_types || ['tasks', 'notes']);
  const [includeSubfolders, setIncludeSubfolders] = useState(criteria.include_subfolders || false);

  const toggleItem = (list, setList, item) => {
    setList(list.includes(item) ? list.filter((i) => i !== item) : [...list, item]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    const built = {};
    if (selectedStatuses.length) built.status = selectedStatuses;
    if (selectedPriorities.length) built.priority = selectedPriorities;
    if (selectedFolderIds.length) {
      built.folder_ids = selectedFolderIds;
      if (includeSubfolders) built.include_subfolders = true;
    }
    if (selectedTagIds.length) built.tag_ids = selectedTagIds;
    if (selectedItemTypes.length && selectedItemTypes.length < 2) {
      built.item_types = selectedItemTypes;
    } else {
      built.item_types = ['tasks', 'notes'];
    }

    onSave({ name: name.trim(), criteria: built });
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <form className="modal-content" onSubmit={handleSubmit}>
          <div className="modal-header">
            <h5 className="modal-title">{view ? 'Edit View' : 'New View'}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">Name</label>
              <input
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Item Types</label>
              <div className="d-flex flex-wrap gap-2">
                {['tasks', 'notes'].map((type) => (
                  <div className="form-check" key={type}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`type-${type}`}
                      checked={selectedItemTypes.includes(type)}
                      onChange={() => toggleItem(selectedItemTypes, setSelectedItemTypes, type)}
                    />
                    <label className="form-check-label" htmlFor={`type-${type}`}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Status</label>
              <div className="d-flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <div className="form-check" key={s}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`status-${s}`}
                      checked={selectedStatuses.includes(s)}
                      onChange={() => toggleItem(selectedStatuses, setSelectedStatuses, s)}
                    />
                    <label className="form-check-label" htmlFor={`status-${s}`}>{s}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Priority</label>
              <div className="d-flex flex-wrap gap-2">
                {PRIORITIES.map((p) => (
                  <div className="form-check" key={p}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`priority-${p}`}
                      checked={selectedPriorities.includes(p)}
                      onChange={() => toggleItem(selectedPriorities, setSelectedPriorities, p)}
                    />
                    <label className="form-check-label" htmlFor={`priority-${p}`}>{p}</label>
                  </div>
                ))}
              </div>
            </div>

            {folders && folders.length > 0 && (
              <div className="mb-3">
                <label className="form-label">Folders</label>
                <div className="d-flex flex-wrap gap-2">
                  {folders.map((f) => (
                    <div className="form-check" key={f.id}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`folder-${f.id}`}
                        checked={selectedFolderIds.includes(f.id)}
                        onChange={() => toggleItem(selectedFolderIds, setSelectedFolderIds, f.id)}
                      />
                      <label className="form-check-label" htmlFor={`folder-${f.id}`}>{f.name}</label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedFolderIds.length > 0 && (
              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="include-subfolders"
                    checked={includeSubfolders}
                    onChange={() => setIncludeSubfolders(!includeSubfolders)}
                  />
                  <label className="form-check-label" htmlFor="include-subfolders">Include subfolders</label>
                </div>
              </div>
            )}

            {tags && tags.length > 0 && (
              <div className="mb-3">
                <label className="form-label">Tags</label>
                <div className="d-flex flex-wrap gap-2">
                  {tags.map((tag) => {
                    const selected = selectedTagIds.includes(tag.id);
                    return (
                      <span
                        key={tag.id}
                        className="badge"
                        style={{
                          backgroundColor: selected ? tag.color : 'transparent',
                          color: selected ? '#fff' : tag.color,
                          border: `2px solid ${tag.color}`,
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                        }}
                        onClick={() => toggleItem(selectedTagIds, setSelectedTagIds, tag.id)}
                      >
                        {tag.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={!name.trim()}>Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
